import { Injectable } from '@angular/core';
import { SurveyService } from '../../../../../core/services/survey/survey.service';
import { OfficeSearchWidgetService } from './office-search.widget.service';
import { VariablesService } from '../../../../../core/services/survey/variables.service';
import { OfficeSearch } from '@tangilla/tg-ng-models';
import { QuestionsService } from '../../../../../core/services/survey/questions.service';
import { ItemValue } from 'survey-core';
import { ErrorsService } from 'src/app/core/services/utils/errors.service';

@Injectable({
  providedIn: 'root'
})
export class OfficeSearchProcessWidgetService {
  officesLimit = 30;

  constructor(private surveyService: SurveyService,
              private variablesService: VariablesService,
              private questionsService: QuestionsService,
              private officeSearchWidgetService: OfficeSearchWidgetService,
              private errorsService: ErrorsService) {
  }

  processOfficesResult(response: any, questionJson: any) {
    let officesData = this.processOfficesResponseData(response);

    if (officesData.length === 0 && this.officeSearchWidgetService.affiliatedOfficeSearch) {
      this.showNoAffiliatedOfficesError();
    }

    if (this.surveyService.formSource === 'dl-transfer') {
      officesData = this.filterPrimaryOfficesOrMatchingDrName(officesData);
    }

    this.surveyService.surveyModel.setVariable('office_search_result', officesData);

    const officeRadioGroupQuestion = this.getOfficesRadioGroupQuestion(questionJson);
    for (const office of officesData) {
      if (this.officeSearchWidgetService.nrdsSearch) {
        this.processNrdsOfficeSearch(office, officeRadioGroupQuestion);
      } else {
        this.processNonNrdsOfficeSearch(office, officeRadioGroupQuestion);
      }
    }

    if (this.questionsService.officeNRDS) {
      this.questionsService.officeNRDS.clearErrors();
    }

    if (officeRadioGroupQuestion) {
      if (officeRadioGroupQuestion.choices.length > this.officesLimit &&
        this.questionsService.officeSearchType?.value !== 'Broker') {

        /*      survey.setVariable('office_request_error', '&#x2757; More than ' + limit + ' active offices match your search, try adding a city to the search. <br />To search for an exact match, enclose the search term in double quotes (").');
              $('#OfficeSearchError').html(survey.getVariable('office_request_error')).show();*/
        this.variablesService.officeValidated = false;
        return false;
      }

      if (officeRadioGroupQuestion.choices.length > 0) {
        /*qOffice.visible = true;*/
        this.variablesService.officeValidated = true;
        return true;
      } else {
        /*qOffice.visible = false;*/
        /*survey.setVariable('office_request_error', 'We have not been able to find an active office based on that search, please try again.');
        $('#OfficeSearchError').html(survey.getVariable('office_request_error')).show();*/
        this.variablesService.officeValidated = false;
        return false;
      }
    }
    return false;
  }

  processOfficesResponseData(data: any) {
    switch (this.surveyService.formSource) {
      case 'Change Brokerage Request':
        return this.surveyService.personRelationships?.length > 0 ?
          data.filter((office: OfficeSearch) => !this.surveyService.personRelationships
            .some(relationship => relationship.office_nrds_id === office.office_nrds_id)) : data;
      default:
        return data;
    }
  }

  private processNrdsOfficeSearch(office: OfficeSearch, officeRadioGroupQuestion: any) {
    if (office.status === 'A' && office.office_nrds_id !== this.variablesService.staffOffice) {
      if (!this.surveyService.drWorkflow ||
        this.questionsService.corporateAffiliate?.value !== 'Yes' ||
        office.association_nrds_id === this.variablesService.associationNrdsId ||
        office.dr_last_name.toLowerCase() === this.questionsService.lastName?.value.toLowerCase()
      ) {
        const text = this.mapNRDSOfficeRadioText(office);
        this.addOfficeToRadioGroup(office, officeRadioGroupQuestion, text);
      }
    }
  }

  private mapNRDSOfficeRadioText(office: any) {
    const drNameDescription = ` (<strong>${office.dr_first_name} ${office.dr_last_name}</strong>)`;
    const addressDescription = `${office.street_line_1} ${office.city} ${office.state}`;

    const actualOfficeIdDescription = this.variablesService.noMlsProductOffered ? `NRDS#${office.office_nrds_id}` : office.office_mls_id ? `MLSID: ${office.office_mls_id}` : `NRDS#${office.office_nrds_id}`;

    return `<strong>${office.business_name}</strong> ${actualOfficeIdDescription}<br>${addressDescription}${drNameDescription}`;
  }

  processNonNrdsOfficeSearch(office: OfficeSearch, questionJson: any) {
    const text = this.mapNonNRDSOfficeRadioText(office);
    this.addOfficeToRadioGroup(office, questionJson, text);
  }

  private mapNonNRDSOfficeRadioText(office: any) {
    const primaryAssociationName = (office.office_primary_board_name || office.primary_assoc_name)?.replace(/[^ -~]+/g, '');
    let licenseNumber = '';
    if (office.dr_license_number !== '[MLSOnly]') {
      if (office.dr_license_number) {
        licenseNumber = ` (<strong>${office.dr_full_name || `${office.dr_first_name} ${office.dr_last_name}`}</strong> Lic#${office.dr_license_number})`;
      } else {
        licenseNumber = ` (<strong>${office.dr_full_name || `${office.dr_first_name} ${office.dr_last_name}`}</strong>)`;
      }
    }
    const addressDescription = office.office_address || `${office.street_line_1} ${office.city} ${office.state}`;

    const actualOfficeIdDescription = this.variablesService.noMlsProductOffered ? `NRDS#${office.office_nrds_id}` : office.office_mls_id ? `MLSID: ${office.office_mls_id}` : `NRDS#${office.office_nrds_id}`;

    return `<strong>${office.business_name}</strong> ${actualOfficeIdDescription}<br />${addressDescription}${licenseNumber}`;
  }

  private addOfficeToRadioGroup(office: OfficeSearch, officeRadioGroupQuestion: any, text: string) {
    if (officeRadioGroupQuestion) {
      if (!officeRadioGroupQuestion.choices) {
        officeRadioGroupQuestion.choices = [];
      }
      const surveyItem = new ItemValue(office.office_nrds_id, text);
      officeRadioGroupQuestion.choices.push(surveyItem);
    }
  }

  private showNoAffiliatedOfficesError() {
    const noAffiliatedOfficesMessage = `The Designated REALTOR® that your License is sponsored by has no other offices affiliated with the ${this.variablesService.getVariable('v-association')}<br/><br/>`;
    const contactBrokerSponsorMessage = 'Please contact your Broker Sponsor for more information.';
    const errorMessage = `${noAffiliatedOfficesMessage}${contactBrokerSponsorMessage}`;

    this.errorsService.exitForm('No Other Affiliated Offices Available', errorMessage);
  }

  private filterPrimaryOfficesOrMatchingDrName(officesData: any) {
    return officesData.filter((office: OfficeSearch) => {
      const isPrimary = office.association_nrds_id === this.variablesService.associationNrdsId;
      const hasSameName = !isPrimary && office.dr_first_name.toLowerCase() === this.surveyService.personRecord?.first_name.toLowerCase() &&
        office.dr_last_name.toLowerCase() === this.surveyService.personRecord?.last_name.toLowerCase();

      return isPrimary || hasSameName;
    });
  }

  getOfficesRadioGroupQuestion(questionJson: any) {
    return questionJson?.questions?.find((question: any) => question.name === 'office');
  }
}
